import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Moon, Sun, Sparkles, Users, ChefHat, PawPrint, PartyPopper, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, formatDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const QUESTIONS = [
    {
        key: "sleep",
        icon: Moon,
        title: "What's your sleep schedule?",
        subtitle: "Be real. We don't judge.",
        options: [
            { v: "early_bird", label: "Early bird 🌅", desc: "Up before 8, in bed by 11" },
            { v: "night_owl", label: "Night owl 🌙", desc: "3AM is my prime time" },
            { v: "flexible", label: "Flexible 🌀", desc: "Depends on the day tbh" },
        ],
    },
    {
        key: "cleanliness",
        icon: Sparkles,
        title: "Cleanliness level?",
        subtitle: "How does your kitchen sink look rn?",
        options: [
            { v: "spotless", label: "Spotless ✨", desc: "You could eat off my floor" },
            { v: "tidy", label: "Tidy 🧺", desc: "Everything has a place" },
            { v: "chill", label: "Chill clean 🌿", desc: "Deep clean weekly, chill daily" },
            { v: "chaotic", label: "Chaotic good 🌪️", desc: "Organized chaos, don't touch" },
        ],
    },
    {
        key: "social",
        icon: PartyPopper,
        title: "Social battery?",
        subtitle: "Rate your extroversion.",
        options: [
            { v: "party_animal", label: "Party animal 🎉", desc: "Where's the pregame?" },
            { v: "social", label: "Social butterfly 🦋", desc: "Love a good hangout" },
            { v: "balanced", label: "Balanced ⚖️", desc: "Some in, some out" },
            { v: "homebody", label: "Homebody 🛋️", desc: "My couch is my BFF" },
        ],
    },
    {
        key: "cooking",
        icon: ChefHat,
        title: "How's your cooking game?",
        subtitle: "Kitchen politics matter.",
        options: [
            { v: "chef", label: "Home chef 🍜", desc: "I meal prep on Sundays" },
            { v: "occasional", label: "Weekend cook 🍳", desc: "Something fancy on weekends" },
            { v: "takeout", label: "Takeout fan 🥡", desc: "DoorDash is my love language" },
            { v: "never", label: "Kitchen ghost 👻", desc: "Microwave is my max" },
        ],
    },
    {
        key: "pets",
        icon: PawPrint,
        title: "Pets — feelings?",
        subtitle: "Furry friends welcome?",
        options: [
            { v: "love_pets", label: "Love 'em 🐕", desc: "I want to pet everything" },
            { v: "ok_pets", label: "Pets are OK 🐈", desc: "Cool if you have one" },
            { v: "no_pets", label: "No pets 🚫", desc: "Prefer a pet-free zone" },
            { v: "allergic", label: "Allergic 🤧", desc: "My body says no" },
        ],
    },
    {
        key: "guests",
        icon: Users,
        title: "Guests over — how often?",
        subtitle: "Include partners, homies, family.",
        options: [
            { v: "frequent", label: "All the time 🎊", desc: "The door is always open" },
            { v: "sometimes", label: "Sometimes 👋", desc: "A few times a month" },
            { v: "rarely", label: "Rarely 🤫", desc: "Special occasions only" },
            { v: "never", label: "Solo vibes 🧘", desc: "Home is my sanctuary" },
        ],
    },
];

export default function VibeQuiz() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [saving, setSaving] = useState(false);
    const [timeLeft, setTimeLeft] = useState(60);

    useEffect(() => {
        if (idx >= QUESTIONS.length) return;
        const t = setInterval(() => setTimeLeft((s) => Math.max(0, s - 1)), 1000);
        return () => clearInterval(t);
    }, [idx]);

    const q = QUESTIONS[idx];
    const progress = (idx / QUESTIONS.length) * 100;

    const pick = async (v) => {
        const next = { ...answers, [q.key]: v };
        setAnswers(next);
        if (idx < QUESTIONS.length - 1) {
            setTimeout(() => setIdx((i) => i + 1), 220);
        } else {
            setSaving(true);
            try {
                const { data } = await api.post("/vibe-quiz", next);
                setUser(data);
                toast.success("Your vibe is locked in ✨");
                navigate("/app/match");
            } catch (err) {
                toast.error(formatDetail(err));
                setSaving(false);
            }
        }
    };

    if (!q) return null;
    const Icon = q.icon;

    return (
        <div className="relative min-h-screen bg-background overflow-hidden">
            <div className="blob bg-primary" style={{ width: 260, height: 260, top: -60, right: -60 }} />
            <div className="blob" style={{ width: 200, height: 200, bottom: 80, left: -30, background: "#f0abfc" }} />
            <div className="grain absolute inset-0" />

            <div className="relative z-10 mx-auto max-w-xl px-6 py-8">
                <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold" data-testid="vibe-step">
                        {idx + 1} / {QUESTIONS.length}
                    </span>
                    <span className={`font-semibold ${timeLeft < 15 ? "text-destructive" : "text-muted-foreground"}`} data-testid="vibe-timer">
                        {timeLeft}s
                    </span>
                </div>
                <Progress value={progress} className="mt-3 h-2 rounded-full" />

                <AnimatePresence mode="wait">
                    <motion.div
                        key={q.key}
                        initial={{ opacity: 0, y: 20, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -12, scale: 0.98 }}
                        transition={{ duration: 0.28 }}
                        className="mt-8"
                    >
                        <div className="h-14 w-14 rounded-2xl bg-primary text-primary-foreground grid place-items-center">
                            <Icon className="h-7 w-7" strokeWidth={2.5} />
                        </div>
                        <h1 className="mt-5 text-3xl sm:text-4xl font-black tracking-tighter" data-testid="vibe-question">
                            {q.title}
                        </h1>
                        <p className="mt-1 text-muted-foreground">{q.subtitle}</p>

                        <div className="mt-6 space-y-3">
                            {q.options.map((o, i) => {
                                const selected = answers[q.key] === o.v;
                                return (
                                    <motion.button
                                        key={o.v}
                                        onClick={() => pick(o.v)}
                                        disabled={saving}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.03 * i }}
                                        whileTap={{ scale: 0.98 }}
                                        data-testid={`vibe-option-${q.key}-${o.v}`}
                                        className={`w-full text-left rounded-3xl border p-5 lift ${
                                            selected
                                                ? "border-primary bg-accent"
                                                : "border-border bg-card hover:border-primary/60 hover:bg-accent/40"
                                        }`}
                                    >
                                        <div className="font-display text-lg font-bold">{o.label}</div>
                                        <div className="text-sm text-muted-foreground mt-0.5">{o.desc}</div>
                                    </motion.button>
                                );
                            })}
                        </div>

                        {idx === QUESTIONS.length - 1 && saving && (
                            <div className="mt-6 text-center text-muted-foreground text-sm">Locking in your vibe…</div>
                        )}
                    </motion.div>
                </AnimatePresence>

                {idx > 0 && !saving && (
                    <Button
                        variant="ghost"
                        onClick={() => setIdx((i) => i - 1)}
                        data-testid="vibe-back-btn"
                        className="mt-6 rounded-full"
                    >
                        ← Back
                    </Button>
                )}
            </div>
        </div>
    );
}
